import { createContext, useCallback, useContext, useMemo, useRef } from "react";

/**
 * What a form with unsaved changes hands up to the page that navigates away from it, for pages
 * that replace or unmount the form without a navigation the form itself can see (e.g. the Bills
 * inbox swapping the bill in its detail pane).
 */
export interface IUnsavedChangesCheck {
    /** Synchronous, for `beforeunload`. */
    hasUnsavedChanges: () => boolean;
    /**
     * Runs `proceed` right away when there is nothing unsaved. Otherwise shows the unsaved-changes
     * prompt: Save runs `proceed` only once the save succeeds, Don't save discards the changes and
     * runs it, and closing the prompt leaves things where they are. `stay` runs when the form stays
     * after all (the prompt was closed, or the save didn't land), for a caller that has to undo
     * whatever asked.
     */
    confirmLeave: (proceed: () => void, stay?: () => void) => void;
}

/**
 * Registers the check and returns its unregister. Unregistering clears only that check, so a
 * form unmounting after its replacement has registered can't wipe the replacement's.
 */
export type TRegisterUnsavedChangesCheck = (check: IUnsavedChangesCheck) => () => void;

export interface IUnsavedChangesCheckRegistry extends IUnsavedChangesCheck {
    register: TRegisterUnsavedChangesCheck;
}

/**
 * Holds the one registered check and asks it on the holder's behalf. Every function returned is
 * stable, so passing `register` down doesn't re-render a memoized child, and with nothing
 * registered there is nothing unsaved.
 */
export const useUnsavedChangesCheckRegistry = (): IUnsavedChangesCheckRegistry => {
    const checkRef = useRef<IUnsavedChangesCheck>();
    const register = useCallback<TRegisterUnsavedChangesCheck>((check) => {
        checkRef.current = check;
        return () => {
            if (checkRef.current === check) {
                checkRef.current = undefined;
            }
        };
    }, []);
    const hasUnsavedChanges = useCallback(() => checkRef.current?.hasUnsavedChanges() ?? false, []);
    const confirmLeave = useCallback((proceed: () => void, stay?: () => void) => {
        if (checkRef.current) {
            checkRef.current.confirmLeave(proceed, stay);
        } else {
            proceed();
        }
    }, []);
    return useMemo(
        () => ({ register, hasUnsavedChanges, confirmLeave }),
        [register, hasUnsavedChanges, confirmLeave],
    );
};

/**
 * The shell's registry (`AppShell` provides it while the shell shows), so a page inside it can
 * register its check and the shell's own chrome (the Research preview switch) can ask it before
 * tearing the page down.
 */
export const UnsavedChangesCheckContext = createContext<IUnsavedChangesCheckRegistry | undefined>(
    undefined,
);

/** The surrounding shell's `register`, or `undefined` outside a shell. */
export const useRegisterUnsavedChangesCheck = () =>
    useContext(UnsavedChangesCheckContext)?.register;
